import { useMemo } from "react";
import { bool, object, string } from "prop-types";
import { observer } from "mobx-react";
import {
  useEstimateEditorSettings,
  useEstimateEditorStore,
  useEstimateStructure,
  useEstimateTableSettings,
  BreakdownCombinedWorkloadBufferTooltip,
  ValueCell,
} from "@tools";
import classnames from "classnames";

export const CombinedTaskWorkType = observer(({
  element,
  expanded,
  className,
  extendPadding,
}) => {
  const { allowEdition, isLibrary, blockExpansions } = useEstimateEditorSettings();
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const { totalWorkloadColumnMaxState } = useEstimateTableSettings();
  
  const { estimateValueLevel, settings, visibility, displayAnyValue } = structure;
  const {
    usedRoundDecimals,
    showBreakdownParentValues,
    maxValuesAsBuffer,
  } = settings;
  const {
    apply,
    hideEstimateCollapse,
  } = visibility;
  const {
    lvl,
    hasChildren,
    combinedDisplayHours,
    combinedDisplayHoursMax,
    combinedUnbufferedDisplayHours,
    combinedUnbufferedDisplayHoursMax,
    showMaxCombinedHours,
    holdsAllHours,
    parentHoldsAllHours,
    areAllWorkTypesOff,
    areAllWorkTypesHidden,
    usedBranchBuffers,
  } = element;
  
  const editable = allowEdition && (!isLibrary || typeof element.id === "number");
  
  // const showHours = !emptyValue && displayAnyValue && !parentHoldsAllHours;
  const showHours = displayAnyValue && !parentHoldsAllHours;
  
  const displayCellContent = lvl <= estimateValueLevel && (!expanded || !hasChildren || holdsAllHours || lvl === estimateValueLevel);
  const blurDisplayedValue = lvl <= estimateValueLevel && showBreakdownParentValues && !displayCellContent;
  // const displayMinMaxSeparator = !emptyValue && showHours;
  
  const allowFocusAction = useMemo(() => (
    !apply || !hideEstimateCollapse
  ), [hideEstimateCollapse, apply]);
  
  const displayUsedBuffers = useMemo(() => (
    displayCellContent && editable && combinedDisplayHours && element.hasUsedWorkTypeBuffers()
  ), [usedBranchBuffers, displayCellContent, combinedDisplayHours, editable]);
  
  const handleFocus = () => {
    if(blockExpansions)
      return;
    structure.sections.find(s => s.id === element.treePath[0])?.setShowCombinedHours(false);
  }
  
  return (
    <ValueCell
      size={totalWorkloadColumnMaxState ? "large" : "medium"}
      editable={ maxValuesAsBuffer ? { min: editable } : editable}
      displayContent={displayCellContent || blurDisplayedValue}
      // displayMinMaxSeparator={displayMinMaxSeparator}
      displayMinMaxSeparator={showHours}
      emptyInputs={!showHours}
      value={combinedDisplayHours}
      valueMax={combinedDisplayHoursMax}
      placeholderMin={combinedUnbufferedDisplayHours}
      placeholderMax={combinedUnbufferedDisplayHoursMax}
      displayMax={showMaxCombinedHours}
      showDecimals
      visible={!areAllWorkTypesOff}
      decimals={usedRoundDecimals}
      focusAction={allowFocusAction ? handleFocus : undefined}
      // messageTitle={t("views.editor.estimate.alert.title")}
      // messageContent={t("views.editor.estimate.alert.content")}
      className={classnames(
        className,
        ((visibilityMode && areAllWorkTypesHidden) || blurDisplayedValue) && "transparent-2"
      )}
      inputClassName={classnames("input-totalHours")}
      extendPadding={extendPadding}
      inputMinTooltipMessage={
        displayUsedBuffers && !maxValuesAsBuffer &&
        <BreakdownCombinedWorkloadBufferTooltip
          element={element}
          cleanValue={combinedUnbufferedDisplayHours}
        />
      }
      inputMaxTooltipMessage={
        displayUsedBuffers &&
        <BreakdownCombinedWorkloadBufferTooltip
          element={element}
          cleanValue={combinedUnbufferedDisplayHoursMax}
          isMax
        />
      }
      navigationKey="estimate"
    />
  );
});

CombinedTaskWorkType.propTypes = {
  element: object.isRequired,
  className: string,
  expanded: bool,
  extendPadding: bool,
};
